import { useRouter } from 'next/router'
import { useEffect, useCallback } from 'react'
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from './LocalStorage'
import { useQueryParams } from './useQueryParams'

const STORAGE_KEY = 'returnToPath'
const QUERY_PARAM = 'returnTo'
export type RedirectFn = (redirectPath: string) => void

export const useRedirectWithReturn = () => {
  const { asPath, push } = useRouter()

  const redirect = useCallback(
    (...pushArgs: Parameters<typeof push>) => {
      setLocalStorageItem(STORAGE_KEY, asPath)
      push(...pushArgs)
    },
    [asPath, push]
  )

  return { redirect }
}

export const useReturnToRouterListener = () => {
  const { params } = useQueryParams()
  const returnToParam = params.get(QUERY_PARAM)

  useEffect(() => {
    if (returnToParam && returnToParam.startsWith('/')) {
      setLocalStorageItem(STORAGE_KEY, returnToParam)
    }
  }, [returnToParam])

  return null
}

export const useReturnToPath = () => {
  const { push } = useRouter()

  const returnToOrRedirect: RedirectFn = (redirectPath) => {
    const returnToPath = getLocalStorageItem(STORAGE_KEY)

    if (returnToPath) {
      removeLocalStorageItem(STORAGE_KEY)
      push(returnToPath)
    } else {
      push(redirectPath)
    }
  }

  const setReturnToPath = (path?: string) => {
    const returnToPath = path ?? window.location.pathname + window.location.search
    if (returnToPath.startsWith('/')) {
      setLocalStorageItem(STORAGE_KEY, returnToPath)
    }
  }

  const clearReturnToPath = () => {
    removeLocalStorageItem(STORAGE_KEY)
  }

  return {
    returnToOrRedirect,
    setReturnToPath,
    clearReturnToPath
  }
}
